import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, getActiveRegistration, setActiveRegistration } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Buildings, Plus, PencilSimple, Trash, ArrowsLeftRight, Check } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const emptyForm = () => ({ id: null, name: "", nerc_id: "", description: "" });

export default function Registrations() {
  const { user } = useAuth();
  const isSuper = !!user?.is_superadmin;
  const [regs, setRegs] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [limit, setLimit] = useState(50);
  const [limitInput, setLimitInput] = useState("50");
  const [form, setForm] = useState(emptyForm());
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delConfirm, setDelConfirm] = useState("");

  const load = useCallback(() => {
    api.get("/registrations").then((r) => {
      setRegs(r.data.registrations || []);
      setLimit(r.data.limit);
      setLimitInput(String(r.data.limit));
      setActiveId(getActiveRegistration() || r.data.active_registration_id || "");
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm()); setOpen(true); };
  const openEdit = (r) => { setForm({ id: r.id, name: r.name, nerc_id: r.nerc_id || "", description: r.description || "" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Registration name is required.");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), nerc_id: form.nerc_id.trim(), description: form.description.trim() };
      if (form.id) await api.put(`/registrations/${form.id}`, payload);
      else await api.post("/registrations", payload);
      setOpen(false); load(); window.dispatchEvent(new Event("claro:registrations-changed")); toast.success(form.id ? "Registration updated" : "Registration created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    const r = delTarget;
    if (!r) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/registrations/${r.id}`);
      const purged = Object.values(data.purged || {}).reduce((a, b) => a + b, 0);
      // if we just deleted the active entity, fall back to the default workspace
      if (getActiveRegistration() === r.id) setActiveRegistration("default");
      setDelTarget(null); setDelConfirm("");
      load(); window.dispatchEvent(new Event("claro:registrations-changed"));
      toast.success(`"${r.name}" deleted${purged ? ` — ${purged} record${purged === 1 ? "" : "s"} purged` : ""}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const switchTo = (r) => {
    setActiveRegistration(r.id);
    toast.success(`Switched to ${r.name}`);
    window.location.assign("/");
  };

  const saveLimit = async () => {
    const v = parseInt(limitInput, 10);
    if (!Number.isFinite(v) || v < 1) return toast.error("Enter a valid limit (≥ 1).");
    try { const { data } = await api.put("/admin/registration-limit", { limit: v }); setLimit(data.limit); setLimitInput(String(data.limit)); toast.success(`Limit set to ${data.limit}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const atLimit = regs.length >= limit;

  return (
    <div className="p-6 space-y-5" data-testid="registrations-manager">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-display font-bold text-xl flex items-center gap-2"><Buildings size={20} weight="fill" />Registered Entities</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Manage every NERC-registered entity in this account. Each registration is a fully isolated workspace
            (its own documents, controls, evidence, RSAWs and profile). Switch the active entity from the sidebar.
          </p>
        </div>
        <Button data-testid="new-registration-btn" onClick={openNew} disabled={atLimit} className="rounded-none bg-primary hover:bg-primary/90"><Plus size={16} className="mr-1" />New Registration</Button>
      </div>

      <div className="border border-border bg-card p-4 flex flex-wrap items-end gap-4" data-testid="registration-limit-card">
        <div>
          <Label>Registration limit</Label>
          <div className="flex items-center gap-2 mt-1">
            {isSuper ? (
              <>
                <Input data-testid="registration-limit-input" type="number" value={limitInput} onChange={(e) => setLimitInput(e.target.value)} className="rounded-none w-28" />
                <Button data-testid="save-registration-limit-btn" onClick={saveLimit} variant="outline" className="rounded-none">Save limit</Button>
              </>
            ) : (
              <span className="font-mono font-bold text-foreground text-lg" data-testid="registration-limit-readonly">{limit}</span>
            )}
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          <span className="font-mono font-bold text-foreground" data-testid="registration-usage">{regs.length}/{limit}</span> registrations used.
          {atLimit && <span className="text-red-600 ml-2 font-semibold">Limit reached — increase to add more.</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {regs.map((r) => {
          const isActive = r.id === activeId;
          return (
            <div key={r.id} data-testid={`registration-${r.id}`} className={`border bg-card p-4 ${isActive ? "border-black" : "border-border"}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-display font-bold text-base flex items-center gap-2">
                    {r.name}
                    {isActive && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase text-green-700 border border-green-300 bg-green-50 px-1.5 py-0.5"><Check size={11} weight="bold" />Active</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 font-mono">{r.nerc_id || "No NERC ID"}</div>
                  {r.description && <p className="text-xs text-muted-foreground mt-1">{r.description}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`edit-registration-${r.id}`} onClick={() => openEdit(r)} className="text-muted-foreground hover:text-foreground p-1"><PencilSimple size={16} /></button>
                  {r.id !== "default" && isSuper && <button data-testid={`delete-registration-${r.id}`} onClick={() => { setDelTarget(r); setDelConfirm(""); }} className="text-muted-foreground hover:text-red-600 p-1"><Trash size={16} /></button>}
                </div>
              </div>
              {!isActive && (
                <Button data-testid={`switch-registration-${r.id}`} onClick={() => switchTo(r)} variant="outline" size="sm" className="rounded-none mt-3">
                  <ArrowsLeftRight size={14} className="mr-1" />Switch to this entity
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none" data-testid="registration-dialog">
          <DialogHeader><DialogTitle>{form.id ? "Edit" : "New"} Registration</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Entity name</Label><Input data-testid="registration-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sunrise Solar Farm 12, LLC" className="rounded-none mt-1" /></div>
            <div><Label>NERC ID (optional)</Label><Input data-testid="registration-nercid-input" value={form.nerc_id} onChange={(e) => setForm({ ...form, nerc_id: e.target.value })} placeholder="e.g. NCR12345" className="rounded-none mt-1" /></div>
            <div><Label>Description (optional)</Label><Input data-testid="registration-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none mt-1" /></div>
          </div>
          <DialogFooter>
            <Button onClick={() => setOpen(false)} variant="outline" className="rounded-none">Cancel</Button>
            <Button data-testid="save-registration-btn" onClick={save} disabled={busy} className="rounded-none bg-primary hover:bg-primary/90">{form.id ? "Save changes" : "Create registration"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) { setDelTarget(null); setDelConfirm(""); } }}>
        <DialogContent className="rounded-none" data-testid="delete-registration-dialog">
          <DialogHeader><DialogTitle className="text-red-700 flex items-center gap-2"><Trash size={18} />Delete “{delTarget?.name}”?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-red-700/90">
              This <span className="font-semibold">permanently deletes the entity and ALL of its data</span> —
              documents, controls, evidence, RSAWs, tracker items, ClaroFlow tickets, CIP-002/004/007/010 records,
              investigations, assessments and profile. This <span className="font-semibold">cannot be undone.</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Requirements under an active Legal Hold will block deletion until released (Administration → Legal Holds).
              Consider downloading a backup first (Administration → Data Management).
            </p>
            <div>
              <Label>Type <span className="font-mono font-bold">DELETE</span> to confirm</Label>
              <Input data-testid="delete-registration-confirm-input" value={delConfirm}
                onChange={(e) => setDelConfirm(e.target.value)} placeholder="Type DELETE"
                className="rounded-none mt-1 border-red-400" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setDelTarget(null); setDelConfirm(""); }} variant="outline" className="rounded-none">Cancel</Button>
            <Button data-testid="confirm-delete-registration-btn" onClick={remove}
              disabled={busy || delConfirm.trim() !== "DELETE"}
              className="rounded-none bg-red-600 hover:bg-red-700">{busy ? "Deleting…" : "Delete entity & all data"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
